
import type { Dispatch } from 'redux';

import { isLocalVideoTrackDesktop } from '../base/tracks/functions';
import { getAudioSettingsVisibility, getVideoSettingsVisibility } from '../settings/functions';

import {
    FULL_SCREEN_CHANGED,
    SET_FULL_SCREEN,
    SET_LABELS_VISIBLE,
    SET_OVERFLOW_DRAWER,
    SET_SHARE_MENU_VISIBLE,
    SET_WHITEBOARD_INSTANCE,
    TOGGLE_ETHERPAD,
    TOGGLE_TOOLBOX_ZOOMIN,
    TOGGLE_TOOLBOX_ZOOMOUT
} from './actionTypes';
import {
    clearToolboxTimeout,
    setToolboxAlwaysVisible,
    setToolboxTimeout,
    setToolboxVisible
} from './actions.native';

export * from './actions.native';
export * from './actions.any';

/**
 * Docks/undocks the Toolbox.
 *
 * @param {boolean} dock - True if dock, false otherwise.
 * @returns {Function}
 */
export function dockToolbox(dock: boolean): Function {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const { timeoutMS, visible } = getState()['features/toolbox'];

        if (dock) {
            // First make sure the toolbox is shown and hovered
            if (!visible) {
                dispatch(manageToolboxVisibility(true, true));
            }
            dispatch(clearToolboxTimeout());
        } else if (visible) {
            dispatch(
                setToolboxTimeout(
                    () => dispatch(manageToolboxVisibility(false, false)),
                    timeoutMS));
        } else {
            dispatch(manageToolboxVisibility(true, false));
        }
    };
}

/**
 * Docks/undocks the Toolbox.
 *
 * @param {boolean} bool - True if dock, false otherwise.
 * @returns {Function}
 */
export function setShareMenuVisible(bool: boolean) {
    return {
        type: SET_SHARE_MENU_VISIBLE,
        visible: bool
    };

}

/**
 * Signals that full screen mode has been entered or exited.
 *
 * @param {boolean} fullScreen - Whether or not full screen mode is currently
 * enabled.
 * @returns {{
 *     type: FULL_SCREEN_CHANGED,
 *     fullScreen: boolean
 * }}
 */
export function fullScreenChanged(fullScreen: boolean) {
    return {
        type: FULL_SCREEN_CHANGED,
        fullScreen
    };
}

/**
 * Hides the toolbox.
 *
 * @param {boolean} force - True to force the hiding of the toolbox without
 * caring about the extended toolbar side panels.
 * @returns {Function}
 */
export function hideToolbox(force: boolean = false): Function {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const { alwaysVisible } = state['features/toolbox'];

        if (alwaysVisible && !force) {
            return;
        }

        if (force) {
            // Force hide regardless of conditions
            dispatch(clearToolboxTimeout());
            dispatch(setToolboxVisible(false));
        } else {
            // Use centralized visibility management
            dispatch(manageToolboxVisibility(false, false));
        }
    };
}

/**
 * Signals a request to enter or exit full screen mode.
 *
 * @param {boolean} fullScreen - True to enter full screen mode, false to exit.
 * @returns {{
 *     type: SET_FULL_SCREEN,
 *     fullScreen: boolean
 * }}
 */
export function setFullScreen(fullScreen: boolean) {
    return {
        type: SET_FULL_SCREEN,
        fullScreen
    };
}

/**
 * Centralized selector to determine if the toolbox should be visible based on current state.
 * This consolidates all the scattered visibility logic throughout the codebase.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean} True if the toolbox should be visible.
 */
export function getToolboxVisibilityState(state: Object) {
    const hangupSettingsVisible = state['features/settings'].hangupSettingsVisible;
    const isAudioVideoSettingsOpen = getAudioSettingsVisibility(state) || getVideoSettingsVisibility(state);

    const {
        alwaysVisible,
        enabled,
        visible,
        hovered,
        visibility,
        timeoutID,
        overflowMenuVisible,
        shareMenuVisible,
        iframeVisible
    } = state['features/toolbox'];

    const { audioSettingsVisible, videoSettingsVisible } = state['features/settings'];
    const chatOpen = state['features/chat'].isOpen;
    const participantsPaneOpen = state['features/participants-pane'].isOpen;
    const dialogOpen = Boolean(state['features/base/dialog'].component);
    const calleeInfoVisible = state['features/invite'].calleeInfoVisible;
    const reactionsOpen = state['features/reactions'].visible;
    const etherpadEditing = state['features/etherpad'].editing;

    // Core toolbox state conditions
    const coreVisible = enabled && (
        alwaysVisible
        || visible
        || timeoutID
        || audioSettingsVisible
        || videoSettingsVisible
    );

    // Dialog and component conditions that force visibility
    const forceVisible = overflowMenuVisible
        || shareMenuVisible
        || chatOpen
        || participantsPaneOpen
        || dialogOpen
        || visible
        || visibility
        || hovered
        || calleeInfoVisible
        || reactionsOpen
        || hangupSettingsVisible
        || isAudioVideoSettingsOpen
        || etherpadEditing
        || iframeVisible;

    const result = coreVisible || forceVisible;

    return result;
}

/**
 * Checks if the toolbox should remain visible due to open dialogs or active components.
 * This is used internally by the visibility management functions.
 *
 * @param {Object} state - The redux state.
 * @returns {boolean} True if the toolbox should stay visible.
 */
function shouldKeepToolboxVisible(state: Object) {
    const isAudioVideoSettingsOpen = getAudioSettingsVisibility(state) || getVideoSettingsVisibility(state);
    const isScreenSharing = isLocalVideoTrackDesktop(state);

    return state['features/toolbox'].overflowMenuVisible
        || state['features/toolbox'].shareMenuVisible
        || state['features/chat'].isOpen
        || state['features/participants-pane'].isOpen
        || state['features/notes']?.isOpen
        || Boolean(state['features/base/dialog'].component)
        || state['features/invite'].calleeInfoVisible
        || isScreenSharing // Only when actually screen sharing
        || isAudioVideoSettingsOpen
        || state['features/etherpad'].editing
        || state['features/toolbox'].iframeVisible;
}

/**
 * Centralized function to manage toolbox visibility with proper synchronization.
 *
 * @param {boolean} visible - Whether to show or hide the toolbox.
 * @param {boolean} hovered - Whether the toolbox is being hovered.
 * @param {number} timeout - Timeout for auto-hide (0 means no timeout).
 * @returns {Function}
 */
export function manageToolboxVisibility(visible: boolean, hovered: boolean = false, timeout: number = 0) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const state = getState();
        const {
            alwaysVisible,
            enabled,
            timeoutMS
        } = state['features/toolbox'];

        if (!enabled) {
            return;
        }

        // Clear any existing timeout
        dispatch(clearToolboxTimeout());

        // Update hover state

        if (visible) {
            // Show toolbox
            dispatch(setToolboxVisible(true));

            // Set timeout for auto-hide if not always visible and no components require visibility
            if (!alwaysVisible && !shouldKeepToolboxVisible(state)) {
                const hideTimeout = timeout || timeoutMS;

                dispatch(
                    setToolboxTimeout(
                        () => dispatch(manageToolboxVisibility(false, false)),
                        hideTimeout
                    )
                );
            }
        } else if (shouldKeepToolboxVisible(state) || hovered) {
            // Keep visible but set a timeout to check again
            dispatch(
                setToolboxTimeout(
                    () => dispatch(manageToolboxVisibility(false, false)),
                    timeoutMS
                )
            );
        } else {
            // Hide toolbox
            dispatch(setToolboxVisible(false));
        }
    };
}

/**
 * Shows the toolbox for specified timeout.
 *
 * @param {number} timeout - Timeout for showing the toolbox.
 * @returns {Function}
 */
export function showToolbox(timeout: number = 0) {
    return manageToolboxVisibility(true, false, timeout);
}

/**
 * Zooms into the Video.
 *
 * @param {number} zoomin - Zooms in the Video .
 * @returns {Function}
 */
export const Zoomin = (zoomin: number = 0) => {
    return {
        type: TOGGLE_TOOLBOX_ZOOMIN,
        zoomin
    };
};

/**
 * Zooms out of the Video.
 *
 * @param {number} zoomout - Zooms in the Video .
 * @returns {Function}
 */
export const Zoomout = (zoomout: number = 0) => {
    return {
        type: TOGGLE_TOOLBOX_ZOOMOUT,
        zoomout
    };
};

/**
 * Signals a request to display overflow as drawer.
 *
 * @param {boolean} displayAsDrawer - True to display overflow as drawer, false to preserve original behaviour.
 * @returns {{
 *     type: SET_OVERFLOW_DRAWER,
 *     displayAsDrawer: boolean
 * }}
 */
export function setOverflowDrawer(displayAsDrawer: boolean) {
    return {
        type: SET_OVERFLOW_DRAWER,
        displayAsDrawer
    };
}

/**
 * Signals a request to display whiteboard.
 *
 * @param {boolean} open - True to display the window of whiteboard.
 * @returns {{
 *     type: SET_WHITEBOARD_INSTANCE,
 * }}
 */
export function sendWhiteBoardInstance(open: boolean) {
    return {
        type: SET_WHITEBOARD_INSTANCE,
        open
    };
}

/**
 * Signals a request to display overflow as drawer.
 *
 * @param {boolean} bool - True to display overflow as drawer, false to preserve original behaviour.
 * @returns {{
 *     type: SET_OVERFLOW_DRAWER,
 *     displayAsDrawer: boolean
 * }}
 */
export function toogleEtherPad(bool: boolean) {
    return {
        type: TOGGLE_ETHERPAD,
        bool
    };
}

/**
 * Signals that toolbar is hovered value should be changed.
 *
 * @param {boolean} hovered - Flag showing whether toolbar is hovered.
 * @returns {{
 *     type: SET_TOOLBAR_HOVERED,
 *     hovered: boolean
 * }}
 */
export function setLabelsVisible(visible: boolean): Object {

    return {
        type: SET_LABELS_VISIBLE,
        visible
    };
}

/** * Toggles the visibility of the Toolbox.
 * @returns {Function}
 */
export function toggleToolboxVisible(state): Function {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const { visible } = getState()['features/toolbox'];

        if (state !== undefined || state !== null) {
            dispatch(setToolboxAlwaysVisible(state));
        } else if (visible) {
            dispatch(setToolboxAlwaysVisible(false));
        } else {
            dispatch(setToolboxAlwaysVisible(true));
        }
    };
}
